import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, UserPlus } from 'lucide-react';

import {
  createCliente,
  fetchCliente,
  updateCliente,
  type Cliente,
  type ClienteCreate,
  type ClienteUpdate,
} from '@/api/clientes';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ErrorState } from '@/components/ui/ErrorState';
import { FormStepper } from '@/components/ui/form-stepper';
import { PageHeader } from '@/components/ui/PageHeader';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { ClienteDocumentosTab } from '../components/ClienteDocumentosTab';
import { buildPayload, STEP_LABELS, TOTAL_STEPS } from './clienteFormUtils';
import { StepComplementar } from './StepComplementar';
import { StepContato } from './StepContato';
import { StepEndereco } from './StepEndereco';
import { StepIdentificacao } from './StepIdentificacao';
import { useClienteForm } from './useClienteForm';

const STEPS = STEP_LABELS.map((label) => ({ label }));

export default function ClienteFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialStep = id ? Number(searchParams.get('step') || 0) : 0;

  // Modo edição: carrega dados do cliente
  const { data: cliente, isLoading, error } = useQuery<Cliente>({
    queryKey: queryKeys.clientes.detail(id!),
    queryFn: () => fetchCliente(id!),
    enabled: !!id,
  });

  // Aguarda carregar antes de montar o form (para inicializar corretamente)
  if (id && isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size={32} label="Carregando cliente..." />
      </div>
    );
  }

  if (id && (error || !cliente)) {
    return (
      <div className="space-y-4">
        <ErrorState
          title="Cliente não encontrado"
          description={error ? classifyError(error).message : 'O cliente solicitado não foi encontrado.'}
          actions={[
            { label: 'Voltar aos clientes', variant: 'outline' as const, onClick: () => navigate('/app/clientes') },
          ]}
        />
      </div>
    );
  }

  return <ClienteFormContent cliente={id ? cliente ?? null : null} initialStep={initialStep} />;
}

function ClienteFormContent({ cliente, initialStep = 0 }: { cliente: Cliente | null; initialStep?: number }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();

  const {
    form,
    errors,
    set,
    currentStep,
    visitedSteps,
    isPF,
    isEditing,
    savedClienteId,
    validateCurrentStep,
    goNext,
    goBack,
    goToStep,
    skip,
    validateAll,
    handleCreated,
  } = useClienteForm(cliente, initialStep);

  const isLastStep = currentStep === TOTAL_STEPS - 1;
  const isFirstStep = currentStep === 0;
  // Pode pular steps 1-3 (opcionais), mas não o 0 (obrigatório) nem o último
  const canSkip = currentStep > 0 && currentStep < TOTAL_STEPS - 1;
  // Cliente já persistido (pré-existente ou recém-criado no step 0)
  const isSaved = !!savedClienteId;

  // --- Mutations ---

  // POST — usado apenas no step 0 em modo criação
  const createMutation = useOfflineMutation<Cliente, ClienteCreate>({
    mutationFn: (payload) => createCliente(payload),
    outbox: {
      type: 'cliente_criar',
      getPayload: (payload) => ({ method: 'POST', url: '/clientes', body: payload }),
      getDescription: (payload) => `Criar cliente ${payload.nome}`,
    },
    invalidateKeys: [
      [...queryKeys.clientes.all],
      [...queryKeys.clientes.counts],
    ],
    onSuccess: (created) => {
      const c = created as Cliente;
      if (c?.id) {
        queryClient.setQueryData(queryKeys.clientes.detail(c.id), c);
        toast.success(`Cliente "${c.nome}" criado`);
        handleCreated(c.id);
      }
    },
    onQueued: () => {
      toast.success(`Cliente "${form.nome}" será criado quando a conexão for restabelecida`);
      navigate('/app/clientes');
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  // PUT — usado para salvar dados dos steps 1-3 ao concluir
  const updateMutation = useOfflineMutation<unknown, { id: string; payload: ClienteUpdate }>({
    mutationFn: ({ id, payload }) => updateCliente(id, payload),
    outbox: {
      type: 'cliente_editar',
      getPayload: ({ id, payload }) => ({ method: 'PUT', url: `/clientes/${id}`, body: payload }),
      getDescription: () => `Editar cliente ${form.nome}`,
    },
    invalidateKeys: [
      [...queryKeys.clientes.all],
      [...queryKeys.clientes.counts],
    ],
    onSuccess: () => {
      showSuccess('Cliente atualizado com sucesso');
      if (savedClienteId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.clientes.detail(savedClienteId) });
      }
      navigate(savedClienteId ? `/app/clientes/${savedClienteId}` : '/app/clientes');
    },
    onQueued: () => navigate(savedClienteId ? `/app/clientes/${savedClienteId}` : '/app/clientes'),
    onError: (err) => notifyMutationError(err, showError),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  function handleFinish() {
    if (!validateAll()) return;
    if (!savedClienteId) return;
    const payload = buildPayload(form, isPF, true) as ClienteUpdate;
    // O escopo só vai quando o usuário o MUDOU: o padrão vazio virava `null` e o PUT tentava
    // tirar do escritório o cliente que acabara de nascer nele (403 para quem não é gestor).
    const salvo =
      queryClient.getQueryData<Cliente>(queryKeys.clientes.detail(savedClienteId)) ?? cliente;
    const persistido = salvo?.escritorio_id ?? null;
    if (!form.escritorio_id || (payload.escritorio_id ?? null) === persistido) {
      delete payload.escritorio_id;
    }
    updateMutation.mutate({ id: savedClienteId, payload });
  }

  function handleNext() {
    // Step 0, modo criação: validar e POST para criar o cliente
    if (currentStep === 0 && !isSaved) {
      if (!validateCurrentStep()) return;
      const payload = buildPayload(form, isPF, false);
      createMutation.mutate(payload as ClienteCreate);
      return;
    }

    // Último step: salvar e navegar
    if (isLastStep) {
      handleFinish();
      return;
    }

    // Steps intermediários: apenas navegar
    goNext();
  }

  const backTo = isEditing && cliente
    ? { label: cliente.nome, path: `/app/clientes/${cliente.id}` }
    : { label: 'Clientes', path: '/app/clientes' };

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEditing ? 'Editar Cliente' : 'Novo Cliente'}
        subtitle={isEditing ? `Editando ${cliente?.nome}` : 'Preencha os dados do cliente'}
        icon={<UserPlus />}
        backTo={backTo}
      />

      <FormStepper
        steps={STEPS}
        currentStep={currentStep}
        visitedSteps={visitedSteps}
        onStepClick={goToStep}
      />

      <Card>
        <CardContent className="pt-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {STEP_LABELS[currentStep]}
          </h2>

          {currentStep === 0 && (
            <StepIdentificacao form={form} errors={errors} set={set} isPF={isPF} />
          )}
          {currentStep === 1 && (
            <StepContato form={form} errors={errors} set={set} />
          )}
          {currentStep === 2 && (
            <StepEndereco form={form} errors={errors} set={set} />
          )}
          {currentStep === 3 && (
            <StepComplementar form={form} set={set} isPF={isPF} />
          )}
          {currentStep === 4 && savedClienteId && (
            <ClienteDocumentosTab clienteId={savedClienteId} clienteNome={form.nome} />
          )}

          {/* Navegação */}
          <div className="mt-8 flex items-center justify-between">
            <div>
              {!isFirstStep && (
                <Button type="button" variant="outline" onClick={goBack}>
                  <ArrowLeft className="mr-1.5 h-4 w-4" />
                  Voltar
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {canSkip && (
                <Button type="button" variant="ghost" onClick={skip}>
                  Pular
                </Button>
              )}
              <Button onClick={handleNext} disabled={isPending}>
                {isPending ? (
                  <Spinner className="mr-2 text-inherit" />
                ) : null}
                {isLastStep ? (
                  <>
                    <Check className="mr-1.5 h-4 w-4" />
                    Concluir
                  </>
                ) : currentStep === 0 && !isSaved ? (
                  <>
                    Criar e continuar
                    <ArrowRight className="ml-1.5 h-4 w-4" />
                  </>
                ) : (
                  <>
                    Próximo
                    <ArrowRight className="ml-1.5 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
